import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { getDarPending, getDarClosed, createDarClosed, updateDarClosed, uploadDarClosedCorrigendum, getSapEmployeeFetch, getDarCharges } from "../../../core/data/redux/darSlice";
import { uploadFile, getPresignedUrl } from "../../../core/data/redux/awsBucketSlice";
import { AppDispatch } from "../../../core/data/redux/store";
import CharacterCounter from "../../../core/common/CharacterCounter";
import moment from "moment";

const DarClosed: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const casenoParam = queryParams.get("caseno");

  const isCreate = !casenoParam;

  const [searchCaseNo, setSearchCaseNo] = useState("");
  const [caseFetched, setCaseFetched] = useState(!isCreate);
  const [initialFetchLoading, setInitialFetchLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    // Inherited from Pending
    caseno: "",
    empno: "",
    daname: "",
    dadesignation: "",
    daempno: "",
    dadepartment: "",
    casetype: "",
    casedescription: "",
    chargesheetdate: "",
    chargesheetfile: "",
    chargesheet_url: "",
    type_of_charges: "",
    charges: [] as number[],

    // Closed specific fields
    outcome_type: "GUILTY", // "EXONERATED" or "GUILTY"
    exoneration_date: "",
    penalty_type: "Minor Penalty",
    penalty_desc: "",
    penalty_start_date: "",
    penalty_end_date: "",
    was_suspended: "No",
    susp_treatment: "",

    // Enquiry Officer (required for Major penalty)
    enqofficer_source: "Internal",
    enqofficer_empno: "",
    enqofficer_name: "",
    enqofficer_designation: "",
    enqofficer_remarks: "",
    enqofficer_attachment: "",
    enqofficer_attachment_url: "",

    // Presenting Officer (required for Major penalty)
    presofficer_source: "Internal",
    presofficer_empno: "",
    presofficer_name: "",
    presofficer_designation: "",
    presofficer_remarks: "",
    presofficer_attachment: "",
    presofficer_attachment_url: "",

    // Appellate Authority (recorded by HR Admin at Closed stage, used later in Appeal)
    aa_source: "Internal",
    aa_empno: "",
    aa_name: "",
    aa_designation: "",

    // Documents
    finalorderfile: "",
    finalorder_url: "",
    corrigendumfile: "",
    corrigendum_url: ""
  });

  const [chargeOptions, setChargeOptions] = useState<any[]>([]);
  const [uploadingFinalOrder, setUploadingFinalOrder] = useState(false);
  const [finalOrderDocName, setFinalOrderDocName] = useState("");

  const [uploadingCorrigendum, setUploadingCorrigendum] = useState(false);
  const [corrigendumDocName, setCorrigendumDocName] = useState("");

  const [fetchingEnqOfficer, setFetchingEnqOfficer] = useState(false);
  const [fetchingPresOfficer, setFetchingPresOfficer] = useState(false);
  const [fetchingAa, setFetchingAa] = useState(false);

  const [uploadingEnqAttachment, setUploadingEnqAttachment] = useState(false);
  const [enqAttachmentName, setEnqAttachmentName] = useState("");
  const [uploadingPresAttachment, setUploadingPresAttachment] = useState(false);
  const [presAttachmentName, setPresAttachmentName] = useState("");

  const todayStr = new Date().toISOString().split("T")[0];

  const isMajorPenalty = formData.penalty_type.toLowerCase().includes("major");

  const handleViewFile = async (fileKey: string) => {
    if (!fileKey) return;
    const toastId = toast.loading("Fetching document...");
    try {
      const url = await dispatch(getPresignedUrl(fileKey)).unwrap();
      toast.dismiss(toastId);
      window.open(url, "_blank");
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Could not fetch document URL.");
    }
  };

  const fetchEnqOfficerDetails = async (empNoArg?: string) => {
    const empNo = empNoArg || formData.enqofficer_empno;
    if (!empNo || empNo.trim().length < 3) {
      toast.warning("Please enter a valid Enquiry Officer Employee No.");
      return;
    }
    const cleanEmpNo = empNo.trim().replace(/^0+/, '');
    const subjectEmpNo = (formData.empno || "").trim().replace(/^0+/, '');
    if (subjectEmpNo && cleanEmpNo === subjectEmpNo) {
      toast.error("The employee against whom the case is initiated cannot be assigned as Enquiry Officer.");
      setFormData(prev => ({
        ...prev,
        enqofficer_empno: "",
        enqofficer_name: "",
        enqofficer_designation: ""
      }));
      return;
    }
    setFetchingEnqOfficer(true);
    try {
      const res = await dispatch(getSapEmployeeFetch({ emp_no: empNo.trim(), role: "EO" })).unwrap();
      if (res?.status === 200 && res?.data) {
        const fetchedEmpNo = (res.data.empno || res.data.emp_no || empNo).toString().trim().replace(/^0+/, '');
        if (subjectEmpNo && fetchedEmpNo === subjectEmpNo) {
          toast.error("The employee against whom the case is initiated cannot be assigned as Enquiry Officer.");
          setFormData(prev => ({
            ...prev,
            enqofficer_empno: "",
            enqofficer_name: "",
            enqofficer_designation: ""
          }));
          return;
        }
        setFormData(prev => ({
          ...prev,
          enqofficer_name: res.data.empname || "",
          enqofficer_designation: res.data.designation || ""
        }));
      } else {
        const errorMsg = res?.message || res?.data?.message || res?.error || "Employee not found.";
        toast.error(errorMsg);
      }
    } catch (err: any) {
      const errorMsg = typeof err === 'string' ? err : (err?.message || err?.error || err?.data?.message || err?.response?.data?.message || "Error fetching Enquiry Officer data.");
      toast.error(errorMsg);
    } finally {
      setFetchingEnqOfficer(false);
    }
  };

  const fetchPresOfficerDetails = async (empNoArg?: string) => {
    const empNo = empNoArg || formData.presofficer_empno;
    if (!empNo || empNo.trim().length < 3) {
      toast.warning("Please enter a valid Presenting Officer Employee No.");
      return;
    }
    const cleanEmpNo = empNo.trim().replace(/^0+/, '');
    const subjectEmpNo = (formData.empno || "").trim().replace(/^0+/, '');
    if (subjectEmpNo && cleanEmpNo === subjectEmpNo) {
      toast.error("The employee against whom the case is initiated cannot be assigned as Presenting Officer.");
      setFormData(prev => ({
        ...prev,
        presofficer_empno: "",
        presofficer_name: "",
        presofficer_designation: ""
      }));
      return;
    }
    setFetchingPresOfficer(true);
    try {
      const res = await dispatch(getSapEmployeeFetch({ emp_no: empNo.trim(), role: "PO" })).unwrap();
      if (res?.status === 200 && res?.data) {
        const fetchedEmpNo = (res.data.empno || res.data.emp_no || empNo).toString().trim().replace(/^0+/, '');
        if (subjectEmpNo && fetchedEmpNo === subjectEmpNo) {
          toast.error("The employee against whom the case is initiated cannot be assigned as Presenting Officer.");
          setFormData(prev => ({
            ...prev,
            presofficer_empno: "",
            presofficer_name: "",
            presofficer_designation: ""
          }));
          return;
        }
        setFormData(prev => ({
          ...prev,
          presofficer_name: res.data.empname || "",
          presofficer_designation: res.data.designation || ""
        }));
      } else {
        const errorMsg = res?.message || res?.data?.message || res?.error || "Employee not found.";
        toast.error(errorMsg);
      }
    } catch (err: any) {
      const errorMsg = typeof err === 'string' ? err : (err?.message || err?.error || err?.data?.message || err?.response?.data?.message || "Error fetching Presenting Officer data.");
      toast.error(errorMsg);
    } finally {
      setFetchingPresOfficer(false);
    }
  };

  const fetchAaDetails = async (empNoArg?: string) => {
    const empNo = empNoArg || formData.aa_empno;
    if (!empNo || empNo.trim().length < 3) {
      toast.warning("Please enter a valid Appellate Authority Employee No.");
      return;
    }
    setFetchingAa(true);
    try {
      const res = await dispatch(getSapEmployeeFetch({ emp_no: empNo.trim(), role: "AA" })).unwrap();
      if (res?.status === 200 && res?.data) {
        setFormData(prev => ({
          ...prev,
          aa_name: res.data.empname || "",
          aa_designation: res.data.designation || ""
        }));
      } else {
        const errorMsg = res?.message || res?.data?.message || res?.error || "Employee not found.";
        toast.error(errorMsg);
      }
    } catch (err: any) {
      const errorMsg = typeof err === 'string' ? err : (err?.message || err?.error || err?.data?.message || err?.response?.data?.message || "Error fetching Appellate Authority data.");
      toast.error(errorMsg);
    } finally {
      setFetchingAa(false);
    }
  };

  const handleEnqAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload PDF files only.");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must not exceed 5MB.");
      e.target.value = "";
      return;
    }
    setUploadingEnqAttachment(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = (reader.result as string).split(",")[1];
      try {
        const awsResult = await dispatch(uploadFile({ filestring: base64, mime_type: file.type, module: "dar" }));
        if (uploadFile.fulfilled.match(awsResult)) {
          const s3Key = awsResult.payload?.key || awsResult.payload?.data?.key || awsResult.payload?.url || awsResult.payload?.data?.url || "";
          if (s3Key) {
            setFormData(prev => ({ ...prev, enqofficer_attachment: s3Key }));
            setEnqAttachmentName(file.name);
            toast.success("Enquiry Officer attachment uploaded successfully.");
          } else {
            toast.error("Failed to retrieve document path from server.");
          }
        } else {
          toast.error("Failed to upload document to AWS.");
        }
      } catch (error) {
        toast.error("Error uploading document.");
      } finally {
        setUploadingEnqAttachment(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handlePresAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload PDF files only.");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must not exceed 5MB.");
      e.target.value = "";
      return;
    }
    setUploadingPresAttachment(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = (reader.result as string).split(",")[1];
      try {
        const awsResult = await dispatch(uploadFile({ filestring: base64, mime_type: file.type, module: "dar" }));
        if (uploadFile.fulfilled.match(awsResult)) {
          const s3Key = awsResult.payload?.key || awsResult.payload?.data?.key || awsResult.payload?.url || awsResult.payload?.data?.url || "";
          if (s3Key) {
            setFormData(prev => ({ ...prev, presofficer_attachment: s3Key }));
            setPresAttachmentName(file.name);
            toast.success("Presenting Officer attachment uploaded successfully.");
          } else {
            toast.error("Failed to retrieve document path from server.");
          }
        } else {
          toast.error("Failed to upload document to AWS.");
        }
      } catch (error) {
        toast.error("Error uploading document.");
      } finally {
        setUploadingPresAttachment(false);
      }
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {

    dispatch(getDarCharges({}))
      .unwrap()
      .then((res: any) => {
        const candidate = res?.data?.data ?? res?.data ?? res;
        setChargeOptions(Array.isArray(candidate) ? candidate : []);
      })
      .catch((err: any) => {
        console.error("Failed to fetch charges", err);
      });
  }, [dispatch]);

  useEffect(() => {
    toast.dismiss();
    if (casenoParam) {
      setInitialFetchLoading(true);
      dispatch(getDarClosed({ caseno: casenoParam }))
        .unwrap()
        .then((res: any) => {
          if (res?.status === 200 && res?.data) {
            const data = res.data;
            const updatedState: any = {
              caseno: data.caseno || "",
              empno: data.empno || "",
              daname: data.daname || "",
              dadesignation: data.dadesignation || "",
              daempno: data.daempno || "",
              dadepartment: data.dadepartment || data.da_department || "",
              casetype: data.casetype || "",
              casedescription: data.casedescription || "",
              chargesheetdate: data.chargesheetdate ? moment(data.chargesheetdate).format("DD MMM YYYY") : "-",
              chargesheetfile: data.chargesheetfile || "",
              chargesheet_url: data.urls?.chargesheet_url || data.chargesheet_url || "",
              charges: Array.isArray(data.charges)
                ? data.charges.map((c: any) => typeof c === "object" ? Number(c.id) : Number(c))
                : Array.isArray(data.charge_ids)
                  ? data.charge_ids.map((c: any) => Number(c))
                  : [],

              outcome_type: data.exonerated_flag ? "EXONERATED" : "GUILTY",
              exoneration_date: data.exoneration_date ? moment(data.exoneration_date).format("YYYY-MM-DD") : "",
              penalty_type: data.penalty_type || "Minor Penalty",
              penalty_desc: data.penalty_desc || "",
              penalty_start_date: data.penalty_start_date ? moment(data.penalty_start_date).format("YYYY-MM-DD") : "",
              penalty_end_date: data.penalty_end_date ? moment(data.penalty_end_date).format("YYYY-MM-DD") : "",
              was_suspended: data.was_suspended ? "Yes" : "No",
              susp_treatment: data.susp_treatment || "",

              enqofficer_source: data.eo_source || (data.eo_empno ? "Internal" : "External"),
              enqofficer_empno: data.eo_empno || "",
              enqofficer_name: data.eo_name || "",
              enqofficer_designation: data.eo_designation || "",
              enqofficer_remarks: data.eo_remarks || "",
              enqofficer_attachment: data.eo_attachment || "",
              enqofficer_attachment_url: data.urls?.eo_attachment_url || "",

              presofficer_source: data.po_source || (data.po_empno ? "Internal" : "External"),
              presofficer_empno: data.po_empno || "",
              presofficer_name: data.po_name || "",
              presofficer_designation: data.po_designation || "",
              presofficer_remarks: data.po_remarks || "",
              presofficer_attachment: data.po_attachment || "",
              presofficer_attachment_url: data.urls?.po_attachment_url || "",

              aa_source: data.aa_source || (data.aa_empno ? "Internal" : "External"),
              aa_empno: data.aa_empno || "",
              aa_name: data.aa_name || "",
              aa_designation: data.aa_designation || "",

              finalorderfile: data.finalorderfile || "",
              finalorder_url: data.urls?.final_order_url || data.url || "",
              corrigendumfile: data.corrigendumfile || "",
              corrigendum_url: data.urls?.corrigendum_url || ""
            };

            // Fetch pending details to populate inherited details
            dispatch(getDarPending({ caseno: data.caseno || casenoParam }))
              .unwrap()
              .then((pendingRes: any) => {
                const pendingDataRaw = pendingRes?.data?.data || pendingRes?.data || {};
                let pendingData = pendingDataRaw;
                if (Array.isArray(pendingDataRaw)) {
                  pendingData = pendingDataRaw.find((item: any) => String(item.version_type || item.versionType || "").toUpperCase() === "CURRENT") || pendingDataRaw[0];
                } else if (Array.isArray(pendingDataRaw?.items)) {
                  pendingData = pendingDataRaw.items.find((item: any) => String(item.version_type || item.versionType || "").toUpperCase() === "CURRENT") || pendingDataRaw.items[0];
                }
                const pendingCharges = Array.isArray(pendingData.charges)
                  ? pendingData.charges.map((c: any) => typeof c === "object" ? Number(c.id) : Number(c))
                  : Array.isArray(pendingData.charge_ids)
                    ? pendingData.charge_ids.map((c: any) => Number(c))
                    : [];

                setFormData({
                  ...updatedState,
                  daname: pendingData.daname || updatedState.daname,
                  dadesignation: pendingData.dadesignation || updatedState.dadesignation,
                  daempno: pendingData.daempno || updatedState.daempno,
                  casetype: pendingData.casetype || updatedState.casetype,
                  casedescription: pendingData.casedescription || updatedState.casedescription,
                  chargesheetdate: pendingData.chargesheetdate ? moment(pendingData.chargesheetdate).format("DD MMM YYYY") : updatedState.chargesheetdate,
                  chargesheetfile: pendingData.chargesheetfile || updatedState.chargesheetfile,
                  chargesheet_url: pendingData.urls?.chargesheet_url || pendingData.chargesheetfile || updatedState.chargesheet_url,
                  type_of_charges: pendingData.type_of_charges || pendingData.charge_type || pendingData.chargetype || updatedState.type_of_charges || "",
                  charges: pendingCharges.length > 0 ? pendingCharges : updatedState.charges
                });

                if (pendingData.chargesheetfile) {
                  dispatch(getPresignedUrl(pendingData.chargesheetfile))
                    .unwrap()
                    .then((url: any) => setFormData(prev => ({ ...prev, chargesheet_url: url as string })))
                    .catch(() => {});
                }

                const daEmpNo = pendingData.daempno || updatedState.daempno;
                if (daEmpNo) {
                  dispatch(getSapEmployeeFetch({ emp_no: daEmpNo, role: "DA" }))
                    .unwrap()
                    .then((sapRes: any) => {
                      if (sapRes?.status === 200 && sapRes?.data) {
                        setFormData(prev => ({
                          ...prev,
                          dadepartment: sapRes.data.deptname || prev.dadepartment
                        }));
                      }
                    })
                    .catch(() => {});
                }
              })
              .catch((err: any) => {
                console.error("Failed to fetch pending case details", err);
                setFormData(updatedState);
              });

            if (data.finalorderfile) {
              setFinalOrderDocName(data.finalorderfile.split('/').pop() || data.finalorderfile);
              if (!data.urls?.final_order_url && !data.url) {
                dispatch(getPresignedUrl(data.finalorderfile)).unwrap()
                  .then((url: any) => setFormData(prev => ({ ...prev, finalorder_url: url as string })))
                  .catch((err: any) => console.error(err));
              }
            }
            if (data.corrigendumfile) {
              setCorrigendumDocName(data.corrigendumfile.split('/').pop() || data.corrigendumfile);
              if (!data.urls?.corrigendum_url) {
                dispatch(getPresignedUrl(data.corrigendumfile)).unwrap()
                  .then((url: any) => setFormData(prev => ({ ...prev, corrigendum_url: url as string })))
                  .catch((err: any) => console.error(err));
              }
            }

            // Also check for Enquiry/Presenting Officer attachments pre-signed URLs
            if (data.eo_attachment) {
              setEnqAttachmentName(data.eo_attachment.split('/').pop() || data.eo_attachment);
              dispatch(getPresignedUrl(data.eo_attachment))
                .unwrap()
                .then((url: any) => setFormData(prev => ({ ...prev, enqofficer_attachment_url: url as string })))
                .catch(() => {});
            }
            if (data.po_attachment) {
              setPresAttachmentName(data.po_attachment.split('/').pop() || data.po_attachment);
              dispatch(getPresignedUrl(data.po_attachment))
                .unwrap()
                .then((url: any) => setFormData(prev => ({ ...prev, presofficer_attachment_url: url as string })))
                .catch(() => {});
            }

            setCaseFetched(true);
          } else {
            toast.error(res?.message || "Failed to fetch closed case details");
            navigate("/dar-cases/closed");
          }
        })
        .catch((err: any) => {
          toast.error(typeof err === 'string' ? err : (err?.message || "Error fetching closed case"));
          navigate("/dar-cases/closed");
        })
        .finally(() => setInitialFetchLoading(false));
    }
  }, [casenoParam, dispatch, navigate]);

  const handleFetchPending = async () => {
    if (!searchCaseNo.trim()) {
      toast.error("Please enter a case number to fetch.");
      return;
    }
    setInitialFetchLoading(true);
    try {
      const response = await dispatch(getDarPending({ caseno: searchCaseNo.trim() })).unwrap();
      const rawResponse = response?.data?.data || response?.data;
      if (response?.status === 200 && rawResponse) {
        let data = rawResponse;
        if (Array.isArray(rawResponse)) {
          data = rawResponse.find((item: any) => String(item.version_type || item.versionType || "").toUpperCase() === "CURRENT") || rawResponse[0];
        } else if (Array.isArray(rawResponse?.items)) {
          data = rawResponse.items.find((item: any) => String(item.version_type || item.versionType || "").toUpperCase() === "CURRENT") || rawResponse.items[0];
        }
        setFormData(prev => ({
          ...prev,
          caseno: data.caseno || searchCaseNo.trim(),
          empno: data.empno || "",
          daname: data.daname || "",
          dadesignation: data.dadesignation || "",
          daempno: data.daempno || "",
          dadepartment: data.dadepartment || data.da_department || "",
          casetype: data.casetype || "",
          casedescription: data.casedescription || "",
          chargesheetdate: data.chargesheetdate ? moment(data.chargesheetdate).format("DD MMM YYYY") : "-",
          chargesheetfile: data.chargesheetfile || "",
          chargesheet_url: data.urls?.chargesheet_url || data.chargesheet_url || "",
          type_of_charges: data.type_of_charges || data.charge_type || data.chargetype || "",
          charges: Array.isArray(data.charges)
            ? data.charges.map((c: any) => typeof c === "object" ? Number(c.id) : Number(c))
            : Array.isArray(data.charge_ids)
              ? data.charge_ids.map((c: any) => Number(c))
              : [],
        }));
        if (data.chargesheetfile) {
          dispatch(getPresignedUrl(data.chargesheetfile)).unwrap()
            .then((url: any) => setFormData(prev => ({ ...prev, chargesheet_url: url as string })))
            .catch((err: any) => console.error(err));
        }
        if (data.daempno) {
          dispatch(getSapEmployeeFetch({ emp_no: data.daempno, role: "DA" }))
            .unwrap()
            .then((daRes: any) => {
              if (daRes?.status === 200 && daRes?.data) {
                setFormData(prev => ({
                  ...prev,
                  daname: daRes.data.empname || prev.daname || "",
                  dadesignation: daRes.data.designation || prev.dadesignation || "",
                  dadepartment: daRes.data.deptname || prev.dadepartment || ""
                }));
              }
            })
            .catch(() => {});
        }
        setCaseFetched(true);
      } else {
        toast.error(response?.message || "Failed to fetch pending case details.");
      }
    } catch (err: any) {
      toast.error(typeof err === 'string' ? err : (err?.message || "Error fetching pending case."));
    } finally {
      setInitialFetchLoading(false);
    }
  };

  const handleFinalOrderUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const targetCaseNo = formData.caseno;
    if (!file) return;

    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload PDF files only.");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must not exceed 5MB.");
      e.target.value = "";
      return;
    }

    if (!targetCaseNo) {
      toast.error("Please fetch a case first before uploading.");
      e.target.value = "";
      return;
    }

    setUploadingFinalOrder(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = (reader.result as string).split(",")[1];
      try {
        const awsResult = await dispatch(uploadFile({ filestring: base64, mime_type: file.type, module: "dar" }));
        if (uploadFile.fulfilled.match(awsResult)) {
          const s3Key = awsResult.payload?.key || awsResult.payload?.data?.key || awsResult.payload?.url || awsResult.payload?.data?.url || "";
          if (s3Key) {
            setFormData(prev => ({ ...prev, finalorderfile: s3Key }));
            setFinalOrderDocName(file.name);
            toast.success("Final Order document uploaded successfully. Don't forget to save changes!");
          } else {
            toast.error("Failed to retrieve document path from server.");
          }
        } else {
          toast.error("Failed to upload document to AWS.");
        }
      } catch (error) {
        toast.error("Error uploading document.");
      } finally {
        setUploadingFinalOrder(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleCorrigendumUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const targetCaseNo = formData.caseno;
    if (!file) return;

    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload PDF files only.");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must not exceed 5MB.");
      e.target.value = "";
      return;
    }

    if (!targetCaseNo) return;

    setUploadingCorrigendum(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = (reader.result as string).split(",")[1];
      try {
        if (!isCreate) {
          // For Update: use dedicated corrigendum endpoint
          const result = await dispatch(uploadDarClosedCorrigendum({ caseno: targetCaseNo, corrigendum_doc: base64, mime_type: file.type }));
          if (uploadDarClosedCorrigendum.fulfilled.match(result)) {
            const s3Key = result.payload?.key || result.payload?.data?.key || result.payload?.url || result.payload?.data?.url || "";
            if (s3Key) {
              setFormData(prev => ({ ...prev, corrigendumfile: s3Key }));
              setCorrigendumDocName(file.name);
              toast.success("Corrigendum uploaded and attached successfully!");
            }
          }
        } else {
          // For Create: use standard AWS endpoint
          const awsResult = await dispatch(uploadFile({ filestring: base64, mime_type: file.type, module: "dar" }));
          if (uploadFile.fulfilled.match(awsResult)) {
            const s3Key = awsResult.payload?.key || awsResult.payload?.data?.key || awsResult.payload?.url || awsResult.payload?.data?.url || "";
            if (s3Key) {
              setFormData(prev => ({ ...prev, corrigendumfile: s3Key }));
              setCorrigendumDocName(file.name);
              toast.success("Corrigendum uploaded successfully.");
            }
          }
        }
      } catch (error) {
        toast.error("Error uploading corrigendum.");
      } finally {
        setUploadingCorrigendum(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async () => {
    if (!formData.caseno) {
      toast.error("Invalid case number.");
      return;
    }
    if (isCreate && !formData.finalorderfile && !finalOrderDocName) {
      toast.error("Final Order Document is required to close the case.");
      return;
    }
    if (formData.outcome_type === "GUILTY") {
      if (!formData.penalty_type) {
        toast.error("Penalty Type is required.");
        return;
      }
      if (!formData.penalty_desc.trim()) {
        toast.error("Penalty descriptive summary is required.");
        return;
      }
      if (!formData.penalty_start_date) {
        toast.error("Effective Start Date is required.");
        return;
      }
    }
    const subjectEmpNo = (formData.empno || "").trim().replace(/^0+/, '');
    if (formData.outcome_type === "GUILTY" && isMajorPenalty) {
      if (formData.enqofficer_empno && subjectEmpNo && formData.enqofficer_empno.trim().replace(/^0+/, '') === subjectEmpNo) {
        toast.error("The employee against whom the case is initiated cannot be assigned as Enquiry Officer.");
        return;
      }
      if (formData.presofficer_empno && subjectEmpNo && formData.presofficer_empno.trim().replace(/^0+/, '') === subjectEmpNo) {
        toast.error("The employee against whom the case is initiated cannot be assigned as Presenting Officer.");
        return;
      }
    }
    setLoading(true);
    try {
      const payload: any = {
        caseno: formData.caseno,
        exonerated_flag: formData.outcome_type === "EXONERATED",
        guilty_flag: formData.outcome_type === "GUILTY",
        exoneration_date: formData.exoneration_date || null,
        penalty_type: formData.penalty_type,
        penalty_category: formData.outcome_type === "GUILTY" ? (isMajorPenalty ? "Major" : "Minor") : null,
        penalty_desc: formData.penalty_desc,
        penalty_start_date: formData.penalty_start_date || null,
        penalty_end_date: formData.penalty_end_date || null,
        suspension_applied: formData.was_suspended === "Yes",
        susp_treatment: formData.susp_treatment,
        aa_source: "Internal",
        aa_empno: formData.aa_empno,
        aa_name: formData.aa_name,
        aa_designation: formData.aa_designation
      };

      if (formData.outcome_type === "GUILTY" && isMajorPenalty) {
        const hasEnq = !!(formData.enqofficer_empno || formData.enqofficer_name);
        payload.eo_source = hasEnq ? "Internal" : null;
        payload.eo_empno = formData.enqofficer_empno || null;
        payload.eo_name = formData.enqofficer_name || null;
        payload.eo_designation = formData.enqofficer_designation || null;
        payload.eo_remarks = formData.enqofficer_remarks || null;
        payload.eo_attachment = formData.enqofficer_attachment || null;

        const hasPres = !!(formData.presofficer_empno || formData.presofficer_name);
        payload.po_source = hasPres ? "Internal" : null;
        payload.po_empno = formData.presofficer_empno || null;
        payload.po_name = formData.presofficer_name || null;
        payload.po_designation = formData.presofficer_designation || null;
        payload.po_remarks = formData.presofficer_remarks || null;
        payload.po_attachment = formData.presofficer_attachment || null;
      }

      if (isCreate) {
        payload.finalorderfile = formData.finalorderfile || finalOrderDocName || "";
        const response = await dispatch(createDarClosed(payload)).unwrap();
        const status = response?.status || response?.data?.status;
        const message = response?.message || response?.data?.message;

        if (status === 200 || status === 201) {
          toast.success(message || "Case successfully closed!");
          navigate("/dar-cases/closed");
        } else {
          toast.error(message || "Failed to close case.");
        }
      } else {
        const response = await dispatch(updateDarClosed(payload)).unwrap();
        const status = response?.status || response?.data?.status;
        const message = response?.message || response?.data?.message;

        if (status === 200 || status === 201) {
          toast.success(message || "Closed case updated successfully!");
          navigate("/dar-cases/closed");
        } else {
          toast.error(message || "Failed to update closed case.");
        }
      }
    } catch (error: any) {
      toast.error(typeof error === 'string' ? error : (error?.message || "Error saving closed case."));
    } finally {
      setLoading(false);
    }
  };

  if (initialFetchLoading) {
    return (
      <div className="page-wrapper">
        <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* HEADER */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{isCreate ? "Close D&AR Case" : "Update Closed Case"}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/dar-cases/closed">D&AR Closed</Link></li>
                <li className="breadcrumb-item active" aria-current="page">{isCreate ? "Close Case" : "Edit"}</li>
              </ol>
            </nav>
          </div>
        </div>

        {/* FETCH SECTION (Create Only) */}
        {isCreate && !caseFetched && (
          <div className="mb-5 p-4 bg-light rounded border">
            <h5 className="mb-3">Fetch Pending Case</h5>
            <div className="d-flex gap-3 align-items-end">
              <div style={{ flex: 1, maxWidth: "400px" }}>
                <label className="form-label">Case Number</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. DAR-2026-HR-007"
                    value={searchCaseNo}
                    onChange={(e) => setSearchCaseNo(e.target.value)}
                  />
              </div>
              <button
                className="btn btn-primary"
                onClick={handleFetchPending}
                disabled={initialFetchLoading}
              >
                <i className="ti ti-search me-2"></i> Fetch Details
              </button>
            </div>
          </div>
        )}

        {/* FORM SECTION */}
        {caseFetched && (
          <div className="card shadow-sm border-0" style={{ borderRadius: '8px' }}>
            <div className="card-body p-4">

              {/* SECTION 1: Case Identity */}
              <h5 className="mb-3 text-muted">Section 1: Case Identity (Inherited)</h5>
              <div className="d-flex flex-wrap gap-4 mb-4">
                <div style={{ minWidth: 260 }} className="flex-fill">
                  <label className="form-label">Case No</label>
                  <input type="text" className="form-control bg-light" value={formData.caseno} disabled />
                </div>
                <div style={{ minWidth: 260 }} className="flex-fill">
                  <label className="form-label">Employee No</label>
                  <input type="text" className="form-control bg-light" value={formData.empno} disabled />
                </div>
              </div>

              {/* SECTION 2: DA Details */}
              <h5 className="mb-3 mt-4 text-muted">Section 2: DA Details (Inherited)</h5>
              <div className="d-flex flex-wrap gap-4 mb-4">
                <div style={{ minWidth: 260 }} className="flex-fill">
                  <label className="form-label">DA Employee No</label>
                  <input className="form-control bg-light" value={formData.daempno} disabled />
                </div>
                <div style={{ minWidth: 260 }} className="flex-fill">
                  <label className="form-label">DA Name</label>
                  <input className="form-control bg-light" value={formData.daname} disabled />
                </div>
                <div style={{ minWidth: 260 }} className="flex-fill">
                  <label className="form-label">DA Designation</label>
                  <input className="form-control bg-light" value={formData.dadesignation} disabled />
                </div>
                <div style={{ minWidth: 260 }} className="flex-fill">
                  <label className="form-label">Department</label>
                  <input className="form-control bg-light" value={formData.dadepartment} disabled />
                </div>
              </div>

              {/* SECTION 3: Case Info */}
              <h5 className="mb-3 mt-4 text-muted">Section 3: Case Info (Inherited)</h5>
              <div className="mb-4">
                <label className="form-label">Case Type</label>
                <input className="form-control bg-light" value={formData.casetype} disabled />
              </div>

              {/* SECTION 4: Charge Sheet Details */}
              <h5 className="mb-3 mt-4 text-muted">Section 4: Charge Sheet Details (Inherited)</h5>
              <div className="card bg-light border-0 rounded-4 mb-4">
                <div className="card-body p-4">
                  <div className="row g-4">
                    <div className="col-md-4">
                      <div className="text-muted small fw-medium mb-1">Type of Charges</div>
                      <div className="fw-semibold text-dark">{formData.type_of_charges || "-"}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted small fw-medium mb-1">Date of Charge Sheet Issuance</div>
                      <div className="fw-semibold text-dark">{formData.chargesheetdate || "-"}</div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted small fw-medium mb-1">Charge Sheet Document</div>
                      {formData.chargesheet_url || formData.chargesheetfile ? (
                        <div className="d-flex align-items-center gap-3 mt-1">
                          <a
                            href={formData.chargesheet_url}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2"
                            style={{ width: "fit-content" }}
                          >
                            <i className="ti ti-eye"></i> View Document
                          </a>
                        </div>
                      ) : (
                        <div className="text-muted small">No file attached</div>
                      )}
                    </div>
                    <div className="col-md-12">
                      <div className="text-muted small fw-medium mb-2">Charges</div>
                      {formData.charges && formData.charges.length > 0 ? (
                        <div className="list-group list-group-flush border rounded-3 overflow-hidden shadow-none mb-0">
                          {chargeOptions
                            .filter((opt: any) => {
                              const val = typeof opt === "object" ? Number(opt.id) : opt;
                              return formData.charges.map(Number).includes(val);
                            })
                            .map((charge: any, idx: number) => (
                              <div key={idx} className="list-group-item text-dark fw-medium d-flex align-items-start gap-2 bg-light-subtle py-2">
                                <span className="text-secondary fw-bold" style={{ minWidth: "20px" }}>{idx + 1}.</span>
                                <span>{charge.charge_description || charge.name}</span>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <div className="text-muted small">No charges added</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <hr className="my-5" />

              {/* SECTION 5: Outcome & Penalty */}
              <h5 className="mb-3 text-primary fw-bold">Section 5: Case Resolution Registry</h5>

              <div className="mb-4">
                <label className="form-label fw-bold text-dark">Outcome Type:</label>
                <div className="d-flex gap-4">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="outcome_type"
                      id="outcome_exonerated"
                      value="EXONERATED"
                      checked={formData.outcome_type === "EXONERATED"}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label fw-medium" htmlFor="outcome_exonerated">
                      EXONERATED (Clears Charges)
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="outcome_type"
                      id="outcome_guilty"
                      value="GUILTY"
                      checked={formData.outcome_type === "GUILTY"}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label fw-medium" htmlFor="outcome_guilty">
                      Penalty (Sustains Charges)
                    </label>
                  </div>
                </div>
              </div>

              <div className="card bg-light border-0 rounded-4 mb-4">
                <div className="card-body p-4">
                  {formData.outcome_type === "EXONERATED" && (
                    <div className="row">
                      <div className="col-md-6">
                        <label className="form-label">Date of Exoneration <span className="text-danger">*</span></label>
                        <input
                          type="date"
                          className="form-control"
                          name="exoneration_date"
                          value={formData.exoneration_date}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  )}

                  {formData.outcome_type === "GUILTY" && (
                    <>
                      <div className="row mb-4">
                        <div className="col-md-12">
                          <label className="form-label">Penalty Tier Type Selection <span className="text-danger">*</span></label>
                          <select
                            className="form-select"
                            name="penalty_type"
                            value={formData.penalty_type}
                            onChange={handleInputChange}
                          >
                            <option value="Minor Penalty">Minor Penalty</option>
                            <option value="Major Penalty">Major Penalty</option>
                          </select>
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="form-label">Penalty Descriptive Summary Ledger <span className="text-danger">*</span></label>
                        <textarea
                          className="form-control"
                          rows={3}
                          name="penalty_desc"
                          value={formData.penalty_desc}
                          onChange={handleInputChange}
                          placeholder="E.g., Reduction to a lower stage in the time-scale of pay for a fixed duration of 2 years."
                          maxLength={1000}
                        />
                        <CharacterCounter value={formData.penalty_desc} maxLength={1000} />
                      </div>

                      <div className="row mb-4">
                        <div className="col-md-6">
                          <label className="form-label">Effective Start Date <span className="text-danger">*</span></label>
                          <input
                            type="date"
                            className="form-control"
                            name="penalty_start_date"
                            value={formData.penalty_start_date}
                            onChange={handleInputChange}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Enforcement Expiry Date <span className="text-danger">*</span></label>
                          <input
                            type="date"
                            className="form-control"
                            name="penalty_end_date"
                            value={formData.penalty_end_date}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <div className="mb-2">
                        <label className="form-label fw-bold">Whether Employee Placed Under Suspension or Not</label>
                        <div className="d-flex gap-4 mb-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="was_suspended"
                              id="susp_yes"
                              value="Yes"
                              checked={formData.was_suspended === "Yes"}
                              onChange={handleInputChange}
                            />
                            <label className="form-check-label" htmlFor="susp_yes">Yes</label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="was_suspended"
                              id="susp_no"
                              value="No"
                              checked={formData.was_suspended === "No"}
                              onChange={handleInputChange}
                            />
                            <label className="form-check-label" htmlFor="susp_no">No</label>
                          </div>
                        </div>

                        {formData.was_suspended === "Yes" && (
                          <div className="row">
                            <div className="col-md-6">
                              <label className="form-label">Treatment of Suspension</label>
                              <select
                                className="form-select"
                                name="susp_treatment"
                                value={formData.susp_treatment}
                                onChange={handleInputChange}
                              >
                                <option value="">Select Treatment...</option>
                                <option value="To be treated as on duty">To be treated as on duty</option>
                                <option value="Not on Duty">Not on Duty</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>

                      {isMajorPenalty && (
                        <div className="mt-4 pt-4 border-top">
                          <h6 className="fw-bold text-dark mb-3">Enquiry Officer &amp; Presenting Officer</h6>

                          {/* Enquiry Officer */}
                          <div className="mb-4">
                            <label className="form-label fw-semibold">Enquiry Officer</label>
                            <div className="row g-3">
                              <div className="col-md-4">
                                <label className="form-label small text-muted">Employee No</label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={formData.enqofficer_empno}
                                    onChange={(e) => setFormData(prev => ({ ...prev, enqofficer_empno: e.target.value }))}
                                    placeholder="e.g. 10234"
                                  />
                                  <button className="btn btn-primary" type="button" onClick={() => fetchEnqOfficerDetails()} disabled={fetchingEnqOfficer}>
                                    {fetchingEnqOfficer ? <i className="ti ti-loader fa-spin"></i> : "Fetch"}
                                  </button>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <label className="form-label small text-muted">Name</label>
                                <input
                                  type="text"
                                  className="form-control bg-light"
                                  value={formData.enqofficer_name}
                                  readOnly
                                  placeholder="Fetched Name"
                                />
                              </div>
                              <div className="col-md-4">
                                <label className="form-label small text-muted">Designation</label>
                                <input
                                  type="text"
                                  className="form-control bg-light"
                                  value={formData.enqofficer_designation}
                                  readOnly
                                  placeholder="Fetched Designation"
                                />
                              </div>
                              <div className="col-md-8">
                                <label className="form-label small text-muted">Remarks</label>
                                <textarea
                                  className="form-control"
                                  rows={2}
                                  value={formData.enqofficer_remarks}
                                  onChange={(e) => setFormData(prev => ({ ...prev, enqofficer_remarks: e.target.value }))}
                                  placeholder="Enter remarks..."
                                  maxLength={1000}
                                />
                                <CharacterCounter value={formData.enqofficer_remarks} maxLength={1000} />
                              </div>
                              <div className="col-md-4">
                                <label className="form-label small text-muted">Attachment</label>
                                <div className="d-flex flex-nowrap align-items-center gap-2 w-100">
                                  <input type="file" id="enqOfficerDoc" className="d-none" accept=".pdf" onChange={handleEnqAttachmentUpload} disabled={uploadingEnqAttachment} />
                                  <button type="button" className="btn btn-outline-primary d-flex align-items-center gap-2" onClick={() => document.getElementById("enqOfficerDoc")?.click()} disabled={uploadingEnqAttachment}>
                                    <i className="ti ti-upload"></i> {uploadingEnqAttachment ? "Uploading..." : "Upload"}
                                  </button>
                                  {enqAttachmentName && !uploadingEnqAttachment && (
                                    <div className="bg-light border border-success-subtle rounded px-2 d-flex align-items-center justify-content-between shadow-sm flex-grow-1 overflow-hidden" style={{ height: "38px", minWidth: 0, maxWidth: "350px" }}>
                                      <div
                                        className="d-flex align-items-center gap-2 text-dark overflow-hidden me-2"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => handleViewFile(formData.enqofficer_attachment)}
                                        title="Click to view file"
                                      >
                                        <i className="ti ti-file-check text-success fs-5"></i>
                                        <div className="text-truncate">
                                          <span className="fw-medium small text-primary text-decoration-underline" title={enqAttachmentName}>{enqAttachmentName}</span>
                                        </div>
                                      </div>
                                      <div className="d-flex align-items-center gap-1">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
                                          style={{ width: "28px", height: "28px", padding: 0 }}
                                          title="View File"
                                          onClick={(e) => { e.preventDefault(); handleViewFile(formData.enqofficer_attachment); }}
                                        >
                                          <i className="ti ti-eye" style={{ fontSize: "1rem" }}></i>
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
                                          style={{ width: "28px", height: "28px", padding: 0 }}
                                          title="Remove File"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            setFormData(prev => ({ ...prev, enqofficer_attachment: "", enqofficer_attachment_url: "" }));
                                            setEnqAttachmentName("");
                                          }}
                                        >
                                          <i className="ti ti-x" style={{ fontSize: "1rem" }}></i>
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Presenting Officer */}
                          <div className="mb-2">
                            <label className="form-label fw-semibold">Presenting Officer</label>
                            <div className="row g-3">
                              <div className="col-md-4">
                                <label className="form-label small text-muted">Employee No</label>
                                <div className="input-group">
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={formData.presofficer_empno}
                                    onChange={(e) => setFormData(prev => ({ ...prev, presofficer_empno: e.target.value }))}
                                    placeholder="e.g. 10234"
                                  />
                                  <button className="btn btn-primary" type="button" onClick={() => fetchPresOfficerDetails()} disabled={fetchingPresOfficer}>
                                    {fetchingPresOfficer ? <i className="ti ti-loader fa-spin"></i> : "Fetch"}
                                  </button>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <label className="form-label small text-muted">Name</label>
                                <input
                                  type="text"
                                  className="form-control bg-light"
                                  value={formData.presofficer_name}
                                  readOnly
                                  placeholder="Fetched Name"
                                />
                              </div>
                              <div className="col-md-4">
                                <label className="form-label small text-muted">Designation</label>
                                <input
                                  type="text"
                                  className="form-control bg-light"
                                  value={formData.presofficer_designation}
                                  readOnly
                                  placeholder="Fetched Designation"
                                />
                              </div>
                              <div className="col-md-8">
                                <label className="form-label small text-muted">Remarks</label>
                                <textarea
                                  className="form-control"
                                  rows={2}
                                  value={formData.presofficer_remarks}
                                  onChange={(e) => setFormData(prev => ({ ...prev, presofficer_remarks: e.target.value }))}
                                  placeholder="Enter remarks..."
                                  maxLength={1000}
                                />
                                <CharacterCounter value={formData.presofficer_remarks} maxLength={1000} />
                              </div>
                              <div className="col-md-4">
                                <label className="form-label small text-muted">Attachment</label>
                                <div className="d-flex flex-nowrap align-items-center gap-2 w-100">
                                  <input type="file" id="presOfficerDoc" className="d-none" accept=".pdf" onChange={handlePresAttachmentUpload} disabled={uploadingPresAttachment} />
                                  <button type="button" className="btn btn-outline-primary d-flex align-items-center gap-2" onClick={() => document.getElementById("presOfficerDoc")?.click()} disabled={uploadingPresAttachment}>
                                    <i className="ti ti-upload"></i> {uploadingPresAttachment ? "Uploading..." : "Upload"}
                                  </button>
                                  {presAttachmentName && !uploadingPresAttachment && (
                                    <div className="bg-light border border-success-subtle rounded px-2 d-flex align-items-center justify-content-between shadow-sm flex-grow-1 overflow-hidden" style={{ height: "38px", minWidth: 0, maxWidth: "350px" }}>
                                      <div
                                        className="d-flex align-items-center gap-2 text-dark overflow-hidden me-2"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => handleViewFile(formData.presofficer_attachment)}
                                        title="Click to view file"
                                      >
                                        <i className="ti ti-file-check text-success fs-5"></i>
                                        <div className="text-truncate">
                                          <span className="fw-medium small text-primary text-decoration-underline" title={presAttachmentName}>{presAttachmentName}</span>
                                        </div>
                                      </div>
                                      <div className="d-flex align-items-center gap-1">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
                                          style={{ width: "28px", height: "28px", padding: 0 }}
                                          title="View File"
                                          onClick={(e) => { e.preventDefault(); handleViewFile(formData.presofficer_attachment); }}
                                        >
                                          <i className="ti ti-eye" style={{ fontSize: "1rem" }}></i>
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
                                          style={{ width: "28px", height: "28px", padding: 0 }}
                                          title="Remove File"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            setFormData(prev => ({ ...prev, presofficer_attachment: "", presofficer_attachment_url: "" }));
                                            setPresAttachmentName("");
                                          }}
                                        >
                                          <i className="ti ti-x" style={{ fontSize: "1rem" }}></i>
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* SECTION 6: Appellate Authority */}
              <h5 className="mb-3 mt-4 text-primary fw-bold">Section 6: Appellate Authority</h5>
              <div className="card bg-light border-0 rounded-4 mb-4">
                <div className="card-body p-4">
                  <div className="text-muted small mb-3">
                    Recorded by HR Admin at case closure; used to pre-fill the employee's Appeal Against Penalty submission.
                  </div>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">AA Employee No <span className="text-danger">*</span></label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={formData.aa_empno}
                          onChange={(e) => setFormData(prev => ({ ...prev, aa_empno: e.target.value }))}
                          placeholder="e.g. 10234"
                        />
                        <button className="btn btn-primary" type="button" onClick={() => fetchAaDetails()} disabled={fetchingAa}>
                          {fetchingAa ? <i className="ti ti-loader fa-spin"></i> : "Fetch"}
                        </button>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">AA Name</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={formData.aa_name}
                        readOnly
                        placeholder="Fetched Name"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">AA Designation</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={formData.aa_designation}
                        readOnly
                        placeholder="Fetched Designation"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 7: Final Order Document */}
              <h5 className="mb-3 mt-4 text-primary fw-bold">Section 7: Final Order Document</h5>
              <div className="mb-4">
                <div className="d-flex align-items-center gap-3">
                  <input
                    type="file"
                    id="finalOrderDoc"
                    className="d-none"
                    accept=".pdf"
                    onChange={handleFinalOrderUpload}
                    disabled={uploadingFinalOrder}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary d-flex align-items-center gap-2"
                    onClick={() => document.getElementById("finalOrderDoc")?.click()}
                    disabled={uploadingFinalOrder}
                  >
                    <i className="ti ti-upload"></i> {uploadingFinalOrder ? "Uploading..." : "Upload Final Order"}
                  </button>
                  {finalOrderDocName && !uploadingFinalOrder && (
                    <div className="bg-light border border-success-subtle rounded px-3 d-flex align-items-center justify-content-between shadow-sm" style={{ height: "38px", minWidth: "300px", maxWidth: "450px" }}>
                      <div
                        className="d-flex align-items-center gap-2 text-dark overflow-hidden me-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleViewFile(formData.finalorderfile)}
                        title="Click to view file"
                      >
                        <i className="ti ti-file-check text-success fs-5"></i>
                        <div className="text-truncate" style={{ maxWidth: '200px' }}>
                          <span className="fw-medium small text-primary text-decoration-underline" title={finalOrderDocName}>{finalOrderDocName}</span>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-1">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary d-flex align-items-center justify-content-center"
                          style={{ width: "28px", height: "28px", padding: 0 }}
                          title="View File"
                          onClick={(e) => { e.preventDefault(); handleViewFile(formData.finalorderfile); }}
                        >
                          <i className="ti ti-eye" style={{ fontSize: "1rem" }}></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
                          style={{ width: "28px", height: "28px", padding: 0 }}
                          title="Remove File"
                          onClick={(e) => {
                            e.preventDefault();
                            setFormData(prev => ({ ...prev, finalorderfile: "", finalorder_url: "" }));
                            setFinalOrderDocName("");
                          }}
                        >
                          <i className="ti ti-x" style={{ fontSize: "1rem" }}></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-muted small mt-2">
                  Accepted formats: PDF only (Max: 5MB)
                </div>
              </div>

              {/* SECTION 6: Corrigendum Document */}
              {!isCreate && (
                <>
                  <h5 className="mb-3 mt-4 text-primary fw-bold">Section 7: Corrigendum (Optional)</h5>
                  <div className="mb-4">
                    <div className="d-flex align-items-center gap-3">
                      <input
                        type="file"
                        id="corrigendumDoc"
                        className="d-none"
                        accept=".pdf"
                        onChange={handleCorrigendumUpload}
                        disabled={uploadingCorrigendum}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary d-flex align-items-center gap-2"
                        onClick={() => document.getElementById("corrigendumDoc")?.click()}
                        disabled={uploadingCorrigendum}
                      >
                        <i className="ti ti-upload"></i> {uploadingCorrigendum ? "Uploading..." : "Upload Corrigendum"}
                      </button>
                      {corrigendumDocName && !uploadingCorrigendum && (
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-success d-flex align-items-center gap-1">
                            <i className="ti ti-check-circle"></i> {corrigendumDocName}
                          </span>
                        </div>
                      )}
                      {formData.corrigendum_url && !corrigendumDocName && !uploadingCorrigendum && (
                        <div className="d-flex align-items-center gap-3 ms-2">
                          <a href={formData.corrigendum_url} target="_blank" rel="noreferrer" className="text-primary d-flex align-items-center gap-1">
                            <i className="ti ti-eye"></i> View Current File
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="text-muted small mt-2">
                      Accepted formats: PDF only (Max: 5MB)
                    </div>
                  </div>
                </>
              )}

              {/* SUBMIT */}
              <div className="d-flex justify-content-end gap-3 mt-5">
                <Link to="/dar-cases/closed" className="btn btn-light border px-4 fw-medium">Cancel</Link>
                <button
                  className="btn btn-primary px-4 fw-medium d-flex align-items-center gap-2"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? (
                    <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Saving...</>
                  ) : (
                    <><i className="ti ti-device-floppy"></i> Save</>
                  )}
                </button>
              </div>

            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default DarClosed;
